import { useCallback, useEffect, useState } from "react";
import {
  getPushSubscriptionStatus,
  getVapidPublicKey,
  removePushSubscription,
  savePushSubscription,
} from "../Api/push";

const SERVICE_WORKER_PATH = "/sw.js";

const isSupported = () =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// The VAPID key arrives base64url-encoded; PushManager only accepts raw bytes.
const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);

  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
};

const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  const registration = await navigator.serviceWorker.register(
    SERVICE_WORKER_PATH
  );
  await navigator.serviceWorker.ready;
  return registration;
};

export function usePushNotifications() {
  const supported = isSupported();

  const [permission, setPermission] = useState<NotificationPermission>(
    supported ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  // The browser hands the same subscription to every account signed in on it,
  // so only the backend knows whether this user opted in.
  useEffect(() => {
    if (!supported) return;

    let cancelled = false;

    const sync = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration(
          SERVICE_WORKER_PATH
        );
        const subscription = await registration?.pushManager.getSubscription();

        if (!subscription) {
          if (!cancelled) setSubscribed(false);
          return;
        }

        const active = await getPushSubscriptionStatus(subscription.endpoint);
        if (!cancelled) setSubscribed(active);
      } catch {
        // A missing or unreachable registration just means "not subscribed".
      }
    };

    sync();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const enable = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;

    setLoading(true);
    try {
      const granted = await Notification.requestPermission();
      setPermission(granted);
      if (granted !== "granted") return false;

      const registration = await registerServiceWorker();
      const publicKey = await getVapidPublicKey();
      if (!publicKey) return false;

      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));

      await savePushSubscription(subscription.toJSON());
      setSubscribed(true);
      return true;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const disable = useCallback(async (): Promise<void> => {
    if (!supported) return;

    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration(
        SERVICE_WORKER_PATH
      );
      const subscription = await registration?.pushManager.getSubscription();
      if (!subscription) {
        setSubscribed(false);
        return;
      }

      // Not subscription.unsubscribe(): that is browser-wide and would cut off
      // every other account signed in here.
      await removePushSubscription(subscription.endpoint);
      setSubscribed(false);
    } catch {
      // Leaving the row behind is harmless; it is pruned on the next 410.
    } finally {
      setLoading(false);
    }
  }, [supported]);

  return {
    supported,
    permission,
    subscribed,
    loading,
    blocked: permission === "denied",
    enable,
    disable,
  };
}
